import { doc, getDoc } from 'firebase/firestore'
import {
  ALLOWED_USERS_COLLECTION,
  allowedUserDocId,
  isOwner,
} from '@budgetmonkey/shared'
import { db } from '@/lib/firebase'

/**
 * The client-side allowlist check — for choosing which screen to show, not for
 * control. The gates that actually protect data are `firestore.rules`,
 * `storage.rules`, and the callable guard; this only decides whether to render
 * the app or a "not authorized" notice.
 *
 * It has to *ask* now. The list stopped being a constant when adding a
 * colleague became a write rather than a deploy, so the browser cannot know
 * the answer without a read.
 *
 * The read doubles as the check. `allowedUsers` is readable only by someone
 * already allowlisted, so a caller who is not on the list is refused the
 * lookup — and that refusal is itself the answer. Both a permission error and
 * a missing document mean the same thing here, which is why neither is
 * distinguished.
 */
export async function isAllowlisted(email: string | null | undefined): Promise<boolean> {
  if (email == null) return false

  const id = allowedUserDocId(email)
  // The owner is hardcoded in the rules and cannot be removed by a bad write
  // to that collection, so they are answered without a read — and stay able to
  // sign in and repair things if the list is ever broken.
  if (isOwner(id)) return true

  try {
    const snapshot = await getDoc(doc(db, ALLOWED_USERS_COLLECTION, id))
    return snapshot.exists()
  } catch {
    return false
  }
}

/**
 * Whether an authenticated session must be signed out because its account is
 * not allowlisted. Called only for a user that did authenticate — so a `null`
 * or unknown email is a rejection, not a signed-out state. A rejected account
 * left signed in keeps a usable credential in the browser for someone the app
 * refuses to serve; this is the rule that says tear it down.
 */
export async function rejectedSessionNeedsSignOut(
  email: string | null | undefined,
): Promise<boolean> {
  return !(await isAllowlisted(email))
}
